/**
 * Portfolio Content Schema — v1.1.0
 *
 * 1.1.0 (2026-08-09, D-019): `Story` and `StoryNarrative` added for the Career
 * Stories collection. Purely additive — a new content file and two new types,
 * touching no existing shape — so it lands under the existing 1.1.0 line
 * rather than minting 1.2.0, and every 1.1.0 file still type-checks unchanged.
 *
 * 1.1.0 (2026-08-07, D-014): `Certification.credentialId?` added;
 * `CertificationCategory` gains "cloud" and "event". Additive and
 * backward-compatible — `credentialId` is optional, so 1.0.0 data still
 * type-checks.
 *
 * Type definitions for the content layer of sourishmukherjee.com.
 * Every content JSON file conforms to `ContentFile<T>`: a provenance
 * `meta` block plus a typed `data` payload. React components must
 * consume these types via the loader (`src/content/index.ts`) and
 * never hardcode professional content.
 *
 * Versioning: bump `schemaVersion` (semver) on any breaking shape
 * change and record the migration in DECISIONS.md.
 */

/** Provenance and versioning metadata carried by every content file. */
export interface ContentMeta {
	/** Semver of the content schema this file conforms to. */
	schemaVersion: string;
	/** ISO date (YYYY-MM-DD) of the last content update. */
	updatedAt: string;
	/** The source that wins on conflict for this file (see content/README.md). */
	canonicalSource: string;
	/** All sources consulted when generating this file. */
	sources: string[];
	/**
	 * `YYYY-MM`. The start of the professional timeline, when it is EARLIER than
	 * the earliest employment entry — a part-time beginning that predates the
	 * full-time start recorded on the role (D-080).
	 *
	 * Only `experience.json` carries it, and only the `yearsExperience`
	 * derivation reads it. It exists so the count can begin where the career
	 * began without the number being typed anywhere (D-071), and so the two
	 * facts stay separable: a role's `startDate` is when that JOB started and is
	 * what the card displays.
	 *
	 * **The loader refuses an anchor LATER than the earliest `startDate`**, so
	 * this field can only ever move the count back. Read that constraint as the
	 * point of the field rather than as a safety rail: a value that could move
	 * the count forward would be a way to inflate a headline figure from a place
	 * no reviewer looks.
	 */
	careerStartDate?: string;
	/** Why `careerStartDate` differs from the earliest role. Required with it. */
	careerStartNote?: string;
	/**
	 * Free-text provenance note. Optional, and already carried by three files
	 * before it was ever declared — it reached them through the loader's type
	 * ASSERTION, which suppresses excess-property checking, so the field was
	 * live and unchecked. Declaring it is the fix, not a new capability.
	 *
	 * Where it matters most: recording that `canonicalSource` LOSES on a
	 * named field, so a sync run cannot quietly restore the value an owner
	 * ruling replaced (D-072).
	 */
	note?: string;
}

/** Uniform wrapper for all content JSON files. */
export interface ContentFile<T> {
	meta: ContentMeta;
	data: T;
}

/* ------------------------------------------------------------------ */
/* Profile                                                             */
/* ------------------------------------------------------------------ */

export interface SocialLink {
	id: string;
	label: string;
	url: string;
	/** Primary links surface in the header/hero; others in the footer. */
	primary: boolean;
}

export interface ProfileLocation {
	city: string;
	region: string;
	country: string;
	/** Preformatted display string, e.g. "Kolkata, India". */
	display: string;
}

export interface Profile {
	name: string;
	/** Post-nominal credentials rendered after the name (resume-canonical). */
	postNominals: string[];
	headline: string;
	tagline: string;
	location: ProfileLocation;
	email: string;
	phone: string;
	summary: string;
	socialLinks: SocialLink[];
}

/* ------------------------------------------------------------------ */
/* Experience                                                          */
/* ------------------------------------------------------------------ */

export interface Achievement {
	text: string;
	/** Featured achievements render in compact views; all render in full views. */
	featured: boolean;
}

export interface ExperienceEntry {
	id: string;
	company: string;
	/** The DEPARTING title — what the role was when it ended. */
	title: string;
	/**
	 * An earlier title held in the SAME role, where the owner progressed within
	 * it. Optional because most roles have none, and absent is not "unknown".
	 *
	 * A SEPARATE FIELD RATHER THAN A LONGER `title`, and the reason is that the
	 * two are consumed differently. `/achievements` renders "{title} at
	 * {company}" to date a broadcast, and the departing title is the right one
	 * there; the Experience timeline is where a career progresses and is the
	 * only place the earlier title belongs. Folding both into one string would
	 * put "formerly …" into a sentence about a television segment.
	 */
	formerTitle?: string;
	location: string;
	/** Partial ISO date, YYYY-MM. */
	startDate: string;
	/** Partial ISO date, YYYY-MM — null means "Present". */
	endDate: string | null;
	summary: string;
	/** Filter facets for the Experience Explorer section. */
	tags: string[];
	achievements: Achievement[];
}

/* ------------------------------------------------------------------ */
/* Education                                                           */
/* ------------------------------------------------------------------ */

export interface EducationEntry {
	id: string;
	degree: string;
	field: string;
	institution: string;
	startYear: number;
	endYear: number;
	notes: string | null;
}

/* ------------------------------------------------------------------ */
/* Certifications                                                      */
/* ------------------------------------------------------------------ */

/**
 * "verified"        — issuer and date confirmed from a canonical source.
 * "pending-details" — real credential with missing issuer/date/URL;
 *                     MUST NOT render publicly until verified.
 */
export type CertificationStatus = "verified" | "pending-details";

export type CertificationCategory =
	| "agile"
	| "ai"
	| "project-management"
	| "quality"
	| "analytics"
	| "engineering"
	| "security"
	| "cloud"
	| "event";

export interface Certification {
	id: string;
	name: string;
	issuer: string | null;
	/** Partial ISO date, YYYY-MM, or null when pending verification. */
	issueDate: string | null;
	credentialUrl: string | null;
	/**
	 * Issuer-assigned identifier from the credential itself (schema 1.1.0).
	 * Independent of `credentialUrl`: many issuers mint an ID without a
	 * public verification page.
	 */
	credentialId?: string | null;
	category: CertificationCategory;
	status: CertificationStatus;
	notes: string | null;
}

/** Publish gate: only verified certifications may render. */
export function isPublishableCertification(cert: Certification): cert is Certification & { status: "verified" } {
	return cert.status === "verified";
}

/* ------------------------------------------------------------------ */
/* Skills                                                              */
/* ------------------------------------------------------------------ */

export interface SkillCategory {
	id: string;
	name: string;
	skills: string[];
}

export interface SkillsData {
	/** Headline skills (sourced from the LinkedIn "Top Skills" block). */
	topSkills: string[];
	categories: SkillCategory[];
}

/* ------------------------------------------------------------------ */
/* Awards                                                              */
/* ------------------------------------------------------------------ */

export interface Award {
	id: string;
	title: string;
	issuer: string | null;
	/** YYYY-MM or YYYY. The MOST RECENT conferral — see `alsoConferredOn`. */
	date: string;
	/**
	 * Every EARLIER conferral of the same award, or null where it was conferred
	 * once. Same granularity as `date`.
	 *
	 * A FIELD RATHER THAN A SENTENCE, AND THAT IS THE WHOLE POINT. D-013
	 * recorded that Mentor of the Year was won in December 2023 *and* December
	 * 2024, that `date` could hold only one of them, and that both years would
	 * therefore live in `description`. They did, for a year — where nothing
	 * could check them, count them or render them as dates. D-066 is this
	 * repository's record of what prose does to numbers: the martial-arts page
	 * computed every figure in its stat cards and still opened with "two
	 * decades" against a three-year record.
	 *
	 * With the dates in fields, "conferred twice" is DERIVED by the page from
	 * `1 + alsoConferredOn.length` rather than typed, so it cannot disagree with
	 * the dates beside it.
	 */
	alsoConferredOn: string[] | null;
	/** Short qualifier line, e.g. "Enterprise Infrastructure & Automation". */
	context: string;
	description: string;
}

/* ------------------------------------------------------------------ */
/* Products and Projects (split from platforms.json, D-067)            */
/* ------------------------------------------------------------------ */

/**
 * A product in the owned SFT suite.
 *
 * THE FIELD IS `involvement`, NOT `role`, AND THE RENAME IS THE POINT. It
 * holds "Technical Committee" and "Workflow Enhancement" beside "Product
 * Owner" — contribution types, not job titles — and a card reading
 * "Role: Technical Committee" is what a mis-named field looks like once it
 * reaches a page. D-065 is the standing lesson that a display string diverging
 * from the layer is how a section starts lying quietly; naming the field what
 * it actually holds costs nothing and removes the divergence at the source.
 *
 * There is no `category`: every product is enterprise SaaS, and a column with
 * one value is a constant, not data. `Project` keeps its category because four
 * are genuinely in play there.
 */
export interface Product {
	id: string;
	name: string;
	organization: string;
	/** Owner ruling 2026-08-20. Seven values deliberately override resume-master-2026 — see D-067. */
	involvement: string;
	description: string;
	outcomes: string[];
	tags: string[];
}

/**
 * Category of a client or delivery project.
 *
 * `enterprise-saas` is deliberately ABSENT. It was the discriminator that
 * corroborated the products/projects split — all nine products carried it and
 * no project did — so a project arriving with it is a row on the wrong side of
 * the split, and this union turns that into a compile error rather than a
 * silently mis-filed card.
 */
export type ProjectCategory = "healthcare-regulated" | "govtech" | "enterprise-client" | "ai-innovation";

/** A client or delivery build that is not part of the owned SFT product suite. */
export interface Project {
	id: string;
	name: string;
	organization: string;
	/** A genuine job role here, unlike Product.involvement. Resume-canonical. */
	role: string;
	category: ProjectCategory;
	description: string;
	outcomes: string[];
	tags: string[];
}

/* ------------------------------------------------------------------ */
/* Services (Services Offered — capabilities, not delivered work)      */
/* ------------------------------------------------------------------ */

/**
 * Barrel export name in `src/assets/index.ts` for a service's icon.
 *
 * A union rather than a bare `string` on purpose, and it earns its keep twice.
 * A typo in `services.json` fails `tsc` instead of rendering a broken image at
 * runtime — the content layer has no other compile-time link to the asset
 * barrel. And it is the machine-readable record of which files in
 * `src/assets/servicesOffered/` are load-bearing: D-018 removed barrel exports
 * for images nothing rendered, and D-031 deleted the seven that stayed unused.
 * Widening this union is the deliberate act that makes an eighth image shippable.
 */
export type ServiceImageKey =
	| "ProjectManagementLogo"
	| "StrategicPlanningLogo"
	| "SystemDesign"
	| "CloudManagementLogo"
	| "AiVisionsComputer"
	| "TechnologyConsultantLogo"
	| "BusinessAdvisorLogo";

/**
 * One offered capability.
 *
 * Deliberately distinct from `Product` and `Project`: both record a system that
 * was built and carry `outcomes`, while a Service is a capability on offer and claims
 * no specific result. Keeping the shapes apart stops the Services section from
 * drifting into unevidenced outcome claims — the failure that retired the
 * template Services copy in D-017.
 */
export interface Service {
	id: string;
	title: string;
	description: string;
	image: ServiceImageKey;
	/** Optional; no entry carries tags today. Present so adding them is not a schema bump. */
	tags?: string[];
}

/* ------------------------------------------------------------------ */
/* Metrics (Home metrics band / Career Dashboard)                      */
/* ------------------------------------------------------------------ */

/**
 * How a metric's displayed value is produced when it must not be typed.
 *
 * There is exactly one derivation and it exists because the typed version was
 * WRONG. `years-experience` carried `"8+"` as a literal while the earliest
 * `startDate` in experience.json is 2018-09 — 7 years 11 months on 2026-08-21,
 * so the site claimed a year it had not reached, and would have needed a hand
 * edit every September to stay merely accurate. D-066's rule settles it: if the
 * layer can settle a number, derive it; if it cannot, do not assert it.
 */
export type MetricDerivation = "years-since-first-role";

interface MetricBase {
	id: string;
	label: string;
	context: string;
	/** Featured metrics render in the Home metrics band. */
	featured: boolean;
	/**
	 * Why a metric is withheld from the band. REQUIRED on every non-featured
	 * entry and enforced by the loader, so a metric can never sit in this file
	 * looking publishable with nothing recording why it is not — which is the
	 * D-065 shape that hid nine credentials for months.
	 */
	note?: string;
}

/** A metric whose value is a claim written into the layer, e.g. "$3M+", "95%". */
export interface TypedMetric extends MetricBase {
	value: string;
	derivation?: never;
}

/** A metric whose value is computed from other content. Never carries a value. */
export interface DerivedMetric extends MetricBase {
	value?: never;
	derivation: MetricDerivation;
}

/**
 * A metric as the JSON holds it.
 *
 * THE UNION DOCUMENTS THE INVARIANT; IT DOES NOT ENFORCE IT, and the difference
 * was found by testing rather than assumed. The first draft of this comment
 * claimed a derived metric "cannot also carry a typed value" — then a planted
 * entry carrying both was compiled by `tsc --noEmit` without complaint. The
 * loader reaches this JSON through `metricsJson as ContentFile<Metric[]>`, and
 * a type ASSERTION suppresses exactly the excess-property check the union would
 * otherwise perform, so nothing at the JSON boundary was ever going to fail.
 *
 * The binding control is therefore `resolveMetric` in the loader, which throws
 * at module load — and because the pre-render imports the loader, that throw
 * fails `npm run build` rather than shipping. The union still earns its place
 * for any `Metric` constructed in TypeScript, and as the statement of intent
 * the runtime check enforces. **An exit code of 0 is not evidence (D-066): a
 * gate that has not been shown to fail has not been shown to work.**
 */
export type Metric = TypedMetric | DerivedMetric;

/**
 * A metric after the loader has resolved it — `value` is always present.
 *
 * Components consume this, never `Metric`, so no component has to know that a
 * derivation exists or handle a missing value.
 */
export interface ResolvedMetric extends MetricBase {
	value: string;
}

/* ------------------------------------------------------------------ */
/* Leadership & additional experience — REMOVED 2026-08-29 (D-093)     */
/* ------------------------------------------------------------------ */
/* `LeadershipItem` and `leadership.json` are gone, and this note is the
   only thing left of them. The file was imported by the loader and read
   by NO component, so its five entries reached a reader only where some
   OTHER collection happened to carry the same subject — which is the
   D-065 defect, one field further out: a content JSON is not a page.
   D-092 settled the one entry covered nowhere (the 2023 GCELT invited
   workshop, now the fourth entry in `workshops.json`) and that left the
   whole file redundant, which is what Article V.6 is for. The other four
   subjects are carried by `workshops.json` (the two 2026 SFT sessions and
   the Aeonix MERN industrial training) and `martialArts.json` (the NCC
   service). Nothing rendered was lost; the join gate asserts all five
   against the built documents, and it does so from a CLOSED list, because
   the source of that list no longer exists to grow. */

/* ------------------------------------------------------------------ */
/* Career Stories (schema 1.1.0, D-019)                                */
/* ------------------------------------------------------------------ */

/**
 * Situation · Action · Result — the documentary unit of Article I.2: a claim
 * with context, constraints, and consequences rather than a resume bullet.
 */
export interface StoryNarrative {
	situation: string;
	action: string;
	result: string;
}

/**
 * How finished the work in a story actually is.
 *
 * **ABSENT IS THE DEFAULT AND MEANS "COMPLETED WORK".** All 23 entries that
 * predate this field describe things that happened, so absence must keep
 * meaning exactly what it meant before — a field whose absence silently
 * acquired a new meaning would rewrite twenty-three stories without editing
 * one of them.
 *
 * WHY THIS EXISTS AT ALL, AND WHY IT IS NOT JUST A TAG. `Stories.tsx` labels
 * the third narrative block **"Result"**, which is an assertion: it says the
 * thing below it is what happened. For an initiative that is approved and
 * forming, the block below holds a status and an expectation, and a heading
 * reading "Result" would be making the claim the prose is carefully avoiding.
 * That is the D-066 failure exactly — a LABEL beside content, true when it was
 * written and false for the next entry. So the heading is DERIVED from this
 * field rather than typed, and a tag could not do that: tags are display copy
 * the renderer never reads.
 *
 * It is a closed union rather than a free string for the same reason the
 * heading is derived: "proposed" and "in-progress" are states with defined
 * renderings, and anything else would be a claim nobody had checked.
 */
export type StoryStage = "proposed" | "in-progress";

export interface Story {
	id: string;
	title: string;
	/** Human-readable span, e.g. "Dec 2024 – Present" — display copy, not a date. */
	period: string;
	organization: string;
	role: string;
	/** Free-form facet; filter pills derive from the values present in the data. */
	category: string;
	/** Featured stories surface in the Home highlight strip; all render on /stories. */
	featured: boolean;
	tags: string[];
	summary: string;
	narrative: StoryNarrative;
	/**
	 * Present only on work that is NOT finished. Omit it for completed work —
	 * see `StoryStage`, and note that omitting it is not a neutral act of
	 * tidiness: it is the assertion that the story describes something done.
	 */
	stage?: StoryStage;
}

/* ------------------------------------------------------------------ */
/* Martial Arts & Service                                              */
/* ------------------------------------------------------------------ */

/**
 * An optional scanned or photographic artifact attached to any martial-arts
 * record. **Absent is the launch state and the default**, and the page is
 * complete without a single one — the capability ships empty so that adding an
 * image later is a data edit rather than a component change.
 *
 * HOW TO ADD ONE (the whole procedure):
 *   1. REDACT the scan first — see the constraint below. This is not optional.
 *   2. Put the prepared file in `src/assets/martial-arts/`.
 *   3. Export it from `src/assets/index.ts`, as every other asset is.
 *   4. Import it in `src/utils/martialArtsImages.ts` and map it to the record
 *      id. The JSON never holds a path; it holds nothing at all until then.
 *
 * WHY THE INDIRECTION. `src/content/data/*.json` is plain data with no module
 * graph, so a bare path string in it would not reach Vite's asset pipeline —
 * it would ship unhashed, unoptimised, and outside the build's own accounting,
 * which is the shape Article V.7 exists to prevent. An imported binding is
 * hashed into `build/assets/` like every other image on the site.
 *
 * REDACTION CONSTRAINT — binding, and it is why no image ships today.
 * A certificate scan is a document about a person, and these carry more than
 * the achievement: **mask the regimental number, the date of birth, every
 * signature, and any certificate serial number before the file enters this
 * repository.** Names may remain. The owner's regimental number is deliberately
 * absent from this entire content file for the same reason; re-introducing it
 * through an unredacted scan would defeat that by the back door.
 */
export interface MartialArtsImage {
	/** Resolved at build time from the id map, never a path in the JSON. */
	src: string;
	/** Required whenever an image is present — an undescribed scan is not evidence. */
	alt: string;
	imageType: "certificate" | "photo";
}

/** One scored outcome. `position` is null for participation with no placing. */
export interface MartialArtsResult {
	/** "Kumite", "Kata", "Open Kata", a weight class, or the contested event. */
	discipline: string;
	/** 1, 2 or 3 for a podium; null when the entry records participation only. */
	position: number | null;
}

/**
 * A dated competitive or ceremonial record.
 *
 * `level` is OPTIONAL and deliberately so: it is present only where the owner
 * stated it or the event's own title carries it. Three NCC entries have none,
 * and inferring one would be manufacturing a fact about a competition's
 * sanction (II.3).
 */
export interface MartialArtsAchievement {
	id: string;
	event: string;
	/** YYYY. */
	year: string;
	/** YYYY-MM-DD, only where a specific date was recorded. */
	date?: string;
	level?: string;
	results: MartialArtsResult[];
	note?: string;
	image?: MartialArtsImage;
}

/** A rank, belt or dan grade held, with the body that conferred it. */
export interface MartialArtsGrade {
	id: string;
	title: string;
	organisation: string;
	/** YYYY-MM where known. */
	date?: string;
	image?: MartialArtsImage;
}

/** An instructing or officiating role. */
export interface MartialArtsRole {
	id: string;
	title: string;
	organisation: string;
	kind: "instructor" | "official";
	/** Display span, e.g. "2013-2018". */
	years?: string;
	note?: string;
	image?: MartialArtsImage;
}

export interface MartialArtsCamp {
	id: string;
	name: string;
	location?: string;
	year: string;
	note?: string;
	image?: MartialArtsImage;
}

export interface MartialArtsCertificate {
	id: string;
	name: string;
	/** The grade awarded on the certificate, e.g. "A". */
	grade: string;
	year: string;
	image?: MartialArtsImage;
}

/** The NCC's organisational placement. Absent on the martial-art disciplines. */
export interface MartialArtsUnit {
	wing: string;
	directorate: string;
	group: string;
	battalion: string;
	division: string;
}

/**
 * One discipline: a martial art, or the NCC as a service record.
 *
 * The two shapes share a file rather than splitting because the page presents
 * them as one record of the same decade, and every field either side does not
 * use is optional. **No count is stored anywhere** — podium totals, first
 * places and the years-active span are all computed from `achievements` by the
 * page, so this file and `awards.json` cannot drift the way D-065 found
 * `certifications.json` and its bridge had.
 */
export interface MartialArtsDiscipline {
	id: string;
	name: string;
	kind: "martial-art" | "service";
	/** YYYY. */
	practisingSince?: string;
	/** NCC only — the owner recorded a duration, not a span. */
	durationYears?: number;
	/** NCC only. */
	rank?: string;
	unit?: MartialArtsUnit;
	grades?: MartialArtsGrade[];
	roles?: MartialArtsRole[];
	achievements: MartialArtsAchievement[];
	camps?: MartialArtsCamp[];
	certificates?: MartialArtsCertificate[];
}

/* ------------------------------------------------------------------ */
/* Achievements — external recognition (the /achievements route)       */
/* ------------------------------------------------------------------ */

/**
 * ============================================================================
 * A piece of external recognition. Schema 1.3.0, added by D-069.
 * ============================================================================
 *
 * A SEPARATE FILE FROM `awards.json`, DELIBERATELY. An award conferred on a
 * person and a broadcaster filming a company's work are different claims about
 * different subjects, and D-067 is this repository's record of what happens
 * when two different claims share one heading: nine owned products and
 * nineteen client builds competed in one grid until a reader could not tell
 * which was which. `attribution` below is the field that keeps them apart, and
 * it is required rather than defaulted for exactly that reason.
 *
 * WHAT THIS TYPE WILL NOT LET YOU SAY. There is no `featuredPerson`, and
 * `attribution` is a closed union. The owner's ruling on the Doordarshan entry
 * was explicit — the feature belongs to the Aeonix team's work and must not be
 * written as a personal one — so the shape makes the over-claim unrepresentable
 * rather than merely discouraged. A person's connection to the recognition is
 * expressed by `roleReference`, which is an id into `experience.json`: the page
 * renders the title and dates FROM that record, so a role cannot be overstated
 * here without contradicting the file the resume sync owns.
 */
export type AchievementKind = "broadcast-feature";

/**
 * Who the recognition was FOR.
 *
 * `team` — the organisation and the people who did the work.
 * `personal` — the site's owner, named as the subject.
 *
 * Not optional and not defaulted: a missing value would have to mean one of
 * them, and guessing which is the whole failure this field prevents.
 */
export type AchievementAttribution = "team" | "personal";

/**
 * One image belonging to an achievement.
 *
 * `alt` is REQUIRED. An undescribed photograph is decoration, not evidence,
 * and a screen-reader user gets nothing from it — the same rule
 * `MartialArtsImage` states, for the same reason.
 *
 * THERE IS NO `src` HERE. The binary is resolved by id through
 * `src/utils/achievementImages.ts`, never named as a path in this JSON: a
 * content file has no module graph, so a path written here would miss Vite's
 * asset pipeline, ship unhashed or 404, and sit outside the build's own
 * accounting — the class of thing Article V.7 exists to keep out.
 */
export interface AchievementImage {
	id: string;
	alt: string;
}

/**
 * Where an achievement's footage lives.
 *
 * A TAGGED OBJECT, NOT A BARE URL, AND THE TAG IS THE POINT. This field used
 * to be `string | null`, documented as a direct file URL to be played in a
 * `<video>`. The footage is a YouTube video, which cannot be rendered that
 * way at all — so a bare string would have forced the component to SNIFF the
 * URL to decide what to build. Guessing a rendering from a substring is the
 * failure `AchievementAttribution` exists to prevent one field over: state the
 * thing, do not infer it.
 *
 * `provider` is a union of one, like `AchievementKind`. A second arm ships
 * when a second provider does, not before — a union arm with no data and no
 * plan is the shape D-071 found rendering nowhere.
 */
export interface AchievementVideo {
	provider: "youtube";
	/**
	 * The YouTube id alone, never a full embed URL.
	 *
	 * The component composes the `youtube-nocookie.com` embed URL from it, so
	 * the privacy-preserving host and the parameters are decided in ONE place
	 * and cannot be half-applied by a future entry that pasted a share link.
	 */
	videoId: string;
	/** The human watch URL. Rendered as a real link, so it works without JS. */
	watchUrl: string;
	/**
	 * Names the footage. REQUIRED, and it carries real accessibility weight:
	 * it is the `<iframe>`'s `title` — a frame without one is announced as
	 * "frame" and nothing else — and it is the play button's accessible name.
	 */
	title: string;
}

/**
 * NAMED `AchievementEntry`, NOT `Achievement`, AND THAT IS NOT STYLE.
 * `Achievement` is already taken, at the top of this file, for an experience
 * bullet — and TypeScript MERGES two interfaces of the same name in the same
 * module rather than rejecting them. The first draft of this type compiled
 * into a single interface requiring `text`, `featured`, `outlet` and `year`
 * together, and the only reason it surfaced is that the JSON then failed to
 * satisfy the merged shape. Had the two been structurally compatible it would
 * have shipped silently. `ExperienceEntry` and `EducationEntry` already use
 * this suffix; so does this.
 */
export interface AchievementEntry {
	id: string;
	kind: AchievementKind;
	title: string;
	/** The publisher or broadcaster, as the owner confirmed it. */
	outlet: string;
	/**
	 * YYYY. Year only, and the type says so: the owner confirmed 2024 and
	 * recorded the month as unverified, so there is no field here that could
	 * carry a month somebody later assumed.
	 */
	year: string;
	/** The programme or initiative the piece belonged to. */
	programmeContext: string;
	/** Whose work was covered. */
	subject: string;
	/** "National" — never "International" unless a source says so. */
	scope: string;
	attribution: AchievementAttribution;
	/** The plain statement of what happened. Documentary voice, no flourish. */
	summary: string;
	/** Id into `experience.json` — the role held during the covered period. */
	roleReference?: string;
	/** Ids into `projects.json` — the work of that kind the layer records. */
	projectReferences?: string[];
	/** Redaction and identifiability record for the images below. */
	imageNote?: string;
	images: AchievementImage[];
	/**
	 * The footage, or null.
	 *
	 * NULL MUST STAY VALID. It is the correct state for any achievement with no
	 * recording, and the page renders the poster frame and a caption without it
	 * — every gate passes in that state.
	 *
	 * A REFERENCE, NEVER AN IMPORT. An imported file would be content-hashed
	 * into `build/assets`, shipped on every deploy and kept by git forever;
	 * `.gitignore` bars `*.mp4` and friends anywhere under the app for that
	 * reason (D-069).
	 */
	video: AchievementVideo | null;
}

/* -------------------------------------------------------------------------- */
/* Letters of appreciation — the third Recognition section (D-106)            */
/* -------------------------------------------------------------------------- */

/**
 * The scanned document itself.
 *
 * NOT OPTIONAL ON `Appreciation`, and that is the difference from
 * `AwardImage`. An award exists whether or not a certificate survives — two of
 * the four in `awards.json` are podium finishes with nothing to scan. A letter
 * of appreciation IS a document: with no scan there is nothing to publish but
 * the site's own summary of a letter nobody can read, which is a testimonial
 * the site wrote for itself. The owner's standing instruction is that
 * testimonials are not invented, so the schema makes an unevidenced one
 * unrepresentable rather than merely discouraged.
 */
export interface AppreciationImage {
	/** Key into `APPRECIATION_IMAGES` — only consulted once `status` is published. */
	id: string;
	/** The same two-key gate `WorkshopCertificate` uses, for the same reason. */
	status: CertificateStatus;
	/**
	 * Describes the REDACTED image, which is the only one that can ever be
	 * shown. Authored ahead of the file so the file cannot arrive without one.
	 */
	alt: string;
}

/**
 * A letter or certificate whose subject is APPRECIATION of the owner's work,
 * as distinct from an award conferred (`awards.json`) or a credential earned
 * (`certifications.json`, `WorkshopCertificate`).
 *
 * THE DISTINCTION IS NOT A FILING CONVENIENCE, it is what stopped this becoming
 * a fifth entry in `awards.json`. That file's `canonicalSource` is the master
 * resume and every row in it is an award somebody conferred; a letter of thanks
 * appended to it would be a claim the resume does not make, arriving through
 * the file the resume sync owns. D-087 hit the mirror image of this from the
 * other side — it retired the Aeonix letter rather than change
 * `WorkshopCertificate | null` into a list, because the two documents are not
 * two credentials. They are a credential and an appreciation, and each one now
 * has a shape that says which it is.
 *
 * ALMOST NOTHING HERE IS NEW PROSE, DELIBERATELY. `workshopReference` is an id
 * into `workshops.json`, so the title, dates, host and description of the work
 * being appreciated come from the record that already owns them — the
 * `roleReference` pattern `AchievementEntry` established. What this file adds is
 * only what the DOCUMENT says: its own heading, who signed it, when, how it
 * names the owner, and one verbatim sentence.
 */
export interface Appreciation {
	id: string;
	/** The document's own heading, transcribed. Never a title written for it. */
	title: string;
	/** Who issued it. */
	issuer: string;
	/** "YYYY-MM-DD" — the date printed on the document. */
	date: string;
	/**
	 * How the DOCUMENT names the owner's role, verbatim.
	 *
	 * Rendered in quotation marks and attributed to the letter, because it is
	 * the issuer's description rather than the site's. Where it disagrees with
	 * `experience.json` the layer is not wrong — a document records the title
	 * held on the day it was signed, which is why D-077 has the Aeonix role
	 * carrying two.
	 */
	roleStated: string;
	/** What the document attests. Documentary voice, no flourish. */
	summary: string;
	/**
	 * One sentence from the document, VERBATIM and quoted as such.
	 *
	 * A paraphrase here would be the site putting words in an issuer's mouth,
	 * which is the one thing a page of other people's praise must not do. The
	 * gate asserts this string appears on the built page inside quotation marks
	 * and that the component contributes no praise of its own.
	 */
	quote: string;
	/** Id into `workshops.json` — the full record of the work appreciated. */
	workshopReference: string;
	image: AppreciationImage;
}

/* -------------------------------------------------------------------------- */
/* Workshops & Training — the /workshops route (D-076)                        */
/* -------------------------------------------------------------------------- */

/**
 * Whether a workshop's certificate scan may be DISPLAYED.
 *
 * A UNION OF TWO, AND THE DEFAULT IS THE SAFE ONE BY CONSTRUCTION — there is no
 * third value and no optional field, so an entry cannot arrive without an
 * answer. This is the D-069 `AchievementAttribution` shape applied to a
 * different risk: there, the over-claim was made unrepresentable; here, it is
 * "someone adds a certificate and forgets to say whether it was redacted".
 *
 *   · `pending-redaction` — the scan exists and MUST NOT be shown, because it
 *     carries third-party business and personal details that are not the site
 *     owner's to publish. **The specific fields are itemised in D-076 and
 *     deliberately not here:** `docs/` is not bundled, and this comment is —
 *     Vite emits `sourcesContent` into the deployed sourcemap, so every word of
 *     it is fetchable from the live site. D-063 is the record of that (two
 *     people's real profiles were publicly readable from a sourcemap while
 *     rendering nowhere). A list of exactly which sensitive fields sit in a
 *     named file in a public repository is a map, and it does not need to ship.
 *   · `published` — a redacted, web-sized file has been supplied AND exported
 *     through `src/utils/workshopImages.ts`. Both halves are required: the
 *     status alone renders nothing, and a binary alone is never reached.
 */
export type CertificateStatus = "pending-redaction" | "published";

/**
 * Whether a workshop PHOTOGRAPH may be displayed.
 *
 * A closed union with no default and no optional field, for the same reason
 * `CertificateStatus` is one: a photograph cannot enter this layer without
 * somebody answering the question. The safe value is the one you get by
 * failing to think about it, because there is no third state to fall into.
 *
 *   · `withheld` — the file exists and MUST NOT be shown. Every workshop
 *     photograph supplied so far is in this state by owner ruling, and the
 *     reasons are recorded in D-078/D-079 rather than here: `docs/` is not
 *     bundled and this comment is (D-063).
 *   · `published` — a safe, web-sized file has been supplied AND exported
 *     through `src/utils/workshopImages.ts`. Both halves are required.
 */
export type WorkshopImageStatus = "withheld" | "published";

/**
 * One photograph in a workshop's gallery.
 *
 * `alt` IS REQUIRED AND IS AUTHORED BEFORE THE FILE. A gallery entry with no
 * alt text is an image a screen reader announces as its filename, and the
 * filename of a workshop photograph is `WhatsApp Image 2026-08-23 at
 * 02.45.14.jpeg`. Requiring it here means the description exists before the
 * binary does, which is also the only order in which anyone writes a good one.
 */
export interface WorkshopImage {
	/** Key into `WORKSHOP_GALLERY_IMAGES` — only consulted once published. */
	id: string;
	alt: string;
	status: WorkshopImageStatus;
}

/** A credential earned by CONDUCTING a workshop, not by attending one. */
export interface WorkshopCertificate {
	/** Key into `WORKSHOP_IMAGES` — only consulted once `status` is published. */
	id: string;
	title: string;
	issuer: string;
	/** "YYYY-MM-DD". */
	issuedDate: string;
	status: CertificateStatus;
	/**
	 * Describes the REDACTED image, which is the only one that can ever be
	 * shown. Authored ahead of the file so the file cannot arrive without one.
	 */
	alt: string;
}

export interface Workshop {
	id: string;
	/**
	 * The last segment of this entry's own detail route: `/workshops/<slug>`
	 * (D-081).
	 *
	 * STORED, NOT DERIVED FROM `title`. A slug computed from the title is a URL
	 * that silently changes the first time someone rewords a heading — and a URL
	 * is a promise to every link, bookmark and search result that already points
	 * at it. The same reasoning is why `id` is stored rather than derived.
	 *
	 * SEPARATE FROM `id`, TOO, though the two could have been one field. `id` is
	 * an internal key and reads like one (`sft-thinktech-ai-automation-v16`,
	 * carrying a deck version that means nothing to a reader); a slug is part of
	 * the address bar and is the owner's to choose. Collapsing them would make
	 * every internal rename a broken URL.
	 *
	 * The loader rejects a duplicate, an empty, or a non-kebab-case slug at
	 * module load, which fails the build rather than shipping two routes that
	 * pre-render to the same file.
	 */
	slug: string;
	title: string;
	/**
	 * "YYYY-MM", or "YYYY" where the month is not known (D-092).
	 *
	 * The route's ordering is derived from this, never from file order, and a
	 * plain string comparison is chronological for both forms — "2023" sorts
	 * before "2023-05", which is the right answer for an entry known only to the
	 * year.
	 *
	 * YEAR-ONLY IS A PRECISION, NOT A PLACEHOLDER. The GCELT workshop was
	 * recorded as 2023 in `leadership.json` — a file this repository no longer
	 * carries, removed in D-093 once every entry in it was covered elsewhere —
	 * and nothing here, the master resume included, records its month. The alternative was to invent
	 * one, and a fabricated month would ship in a pill, in the detail page's
	 * standfirst and in that route's `<meta name="description">` — which is
	 * D-066's rule exactly: if the layer can settle it, derive it; if it cannot,
	 * do not assert it. The loader rejects any other shape at module load.
	 */
	date: string;
	/** "Speaker", "Trainer" — what the owner did, not an employment title. */
	role: string;
	host: string;
	description: string;
	/** Extra precision the month alone cannot carry, or null. */
	dateDetail: string | null;
	/** Verbatim from the credential, or null where there is no document. */
	statement: string | null;
	certificate: WorkshopCertificate | null;
	/**
	 * The gallery on this workshop's detail route (D-081).
	 *
	 * EMPTY IS A COMPLETE STATE, NOT A PENDING ONE. Every entry ships `[]`
	 * today and the detail pages are finished in that state — the gallery
	 * section simply is not on the page, in the same way `/achievements`
	 * shipped a wired and empty video slot in D-069 and looked complete
	 * because the empty state was designed rather than defaulted. There is no
	 * "photos coming soon": a visitor is not the audience for an internal
	 * queue, and a page that narrates its own gaps is less credible, not more.
	 *
	 * AN ARRAY OF RECORDS RATHER THAN OF FILENAMES. A path written in JSON
	 * never reaches Vite's asset pipeline, so it would ship unhashed or 404
	 * (the note in `src/utils/workshopImages.ts` covers this). These carry the
	 * description and the publish decision; the binary is exported separately
	 * and the two are joined at module load.
	 */
	images: WorkshopImage[];
}
